import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import api from "@/lib/api";

const POLL_INTERVAL = 15000; // how often we check for new real purchases
const MAX_TRACKED = 10; // only ever consider the latest 10 orders

export default function SalesPopup() {
  const seenIds = useRef(new Set());
  const isFirstLoad = useRef(true);

  useEffect(() => {
    let cancelled = false;

    async function pollRecentPurchases() {
      try {
        // Backend should return the latest verified, logged-in (non-guest)
        // orders only — e.g. GET /orders/recent?limit=10
        const { data } = await api.get("/orders/recent", {
          params: { limit: MAX_TRACKED },
        });

        if (cancelled || !Array.isArray(data)) return;

        // Keep only genuine, logged-in customer orders (server should already
        // filter this, but we double-guard on the client too).
        const realOrders = data.filter((o) => o.is_guest === false && o.user_id);

        if (isFirstLoad.current) {
          // On first load, just remember what already happened —
          // don't blast the user with a backlog of toasts.
          realOrders.forEach((o) => seenIds.current.add(o.order_id));
          isFirstLoad.current = false;
          return;
        }

        // Show toasts only for orders we haven't already shown,
        // oldest-first so they feel chronological.
        const fresh = realOrders
          .filter((o) => !seenIds.current.has(o.order_id))
          .reverse();

        fresh.forEach((o) => {
          seenIds.current.add(o.order_id);
          showToast(o);
        });

        // Cap memory of seen ids to the latest window.
        if (seenIds.current.size > MAX_TRACKED * 3) {
          const ids = Array.from(seenIds.current).slice(-MAX_TRACKED * 2);
          seenIds.current = new Set(ids);
        }
      } catch {
        // silently skip this cycle — no fake/sample fallback
      }
    }

    function showToast(o) {
      toast.custom(
        (t) => (
          <div
            className="flex items-start gap-3 bg-background border border-border shadow-lg px-4 py-3 relative max-w-xs"
            data-testid="sales-toast"
          >
            <div className="w-1 h-10 bg-brand-accent flex-shrink-0" />
            <div className="pr-4">
              <div className="ie-overline text-text-secondary">Just purchased</div>
              <div className="font-serif text-base mt-1">
                {o.customer_name} from {o.city}
              </div>
              <div className="text-xs text-text-secondary mt-0.5">{o.item_name}</div>
            </div>
            <button
              onClick={() => toast.dismiss(t)}
              className="absolute top-2 right-2 text-text-secondary hover:text-foreground"
              aria-label="Dismiss"
              data-testid="sales-toast-close"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ),
        { duration: 6000 }
      );
    }

    //pollRecentPurchases();
    //const interval = setInterval(pollRecentPurchases, POLL_INTERVAL);
    return () => {
      cancelled = true;
     // clearInterval(interval);
    };
  }, []);

  return null;
}